import { type TypedDocumentNode, gql } from '@apollo/client';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { HeadlineL } from '@sorare/core/src/atoms/typography';
import Dialog from '@sorare/core/src/components/dialog';
import useMutation from '@sorare/core/src/hooks/graphql/useMutation';

import { RevealPackCards } from '@sorare/marketplace/src/components/commonCard/RevealPackCards';

import type {
  StarterPackOpeningMutation,
  StarterPackOpeningMutationVariables,
} from './__generated__/index.graphql';

const StyledDialog = styled(Dialog)`
  background: linear-gradient(to bottom, #2c2b70 0%, rgba(44, 43, 112, 0) 100%);
  border-radius: var(--double-unit);
  border: 1px solid var(--c-nd-150-opaque);
`;

const CLAIM_FIRST_PACK_MUTATION = gql`
  mutation StarterPackOpeningMutation($input: claimStarterPackInput!) {
    claimStarterPack(input: $input) {
      cards {
        slug
        ...RevealPackCards_anyCardInterface
      }
      currentUser {
        slug
        footballUserProfile {
          id
          commonGameOnboarded
        }
      }
      errors {
        message
        code
      }
    }
  }
  ${RevealPackCards.fragments.anyCardInterface}
` as TypedDocumentNode<
  StarterPackOpeningMutation,
  StarterPackOpeningMutationVariables
>;
type CardsFromMutation = NonNullable<
  StarterPackOpeningMutation['claimStarterPack']
>['cards'];

type Props = {
  onContinue: () => void;
};

export const StarterPackOpeningDialog = ({
  open,
  onClose,
  onContinue,
}: Props & {
  open: boolean;
  onClose: () => void;
}) => {
  const [claimPack] = useMutation(CLAIM_FIRST_PACK_MUTATION, {
    showErrorsWithSnackNotification: true,
  });
  const [cards, setCards] = useState<NonNullable<CardsFromMutation>>([]);

  useEffect(() => {
    const onClaimPack = async () => {
      const { data } = await claimPack({
        variables: {
          input: {
            sport: Sport.FOOTBALL,
            favoriteTeamSlug: 'bayern-munchen-munchen',
            starPlayerSlug: 'jamal-musiala',
          },
        },
      });

      setCards(data?.claimStarterPack?.cards || []);
    };
    if (!cards.length) {
      onClaimPack();
    }
  }, [cards.length, claimPack]);

  return (
    <StyledDialog
      open={open}
      maxWidth="xs"
      fullWidth
      onClose={cards.length ? onContinue : onClose}
      disableBackdropClick
      title={
        <HeadlineL>
          <FormattedMessage
            id="OpenStarterPackPage.title"
            defaultMessage="Open your first pack"
          />
        </HeadlineL>
      }
    >
      <RevealPackCards cards={cards} onContinue={onContinue} />
    </StyledDialog>
  );
};
